import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import api from '../../utils/axiosConfig';
import { locationCoordinates } from './LocationDetails';

const reportKey = data => JSON.stringify([data?.unitStatus?.lastPingTime,
    data?.lastKnownLocation?.Latitude, data?.lastKnownLocation?.Longitude, data?.lastKnownLocation?.EventTime]);

export default function DeviceLocationActions({ unit, data, reload }) {
    const [busy, setBusy] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const operation = useRef(null);
    const alive = useRef(false);
    const latest = useRef({ unit, data });
    latest.current = { unit, data };
    const allowed = value => (value?.unit?.UnitModel === 'myConnect Air' ||  value.data?.unitStatus?.workingMode === 'advanced') &&
        value.data?.unitStatus?.UnitSerNum === value.unit?.UnitSerNum &&
        value.data?.userMap?.unit === value.unit?.UnitSerNum &&
        ['Interactive', 'Admin'].includes(value.data?.userMap?.userType) &&
        Boolean(locationCoordinates(value.data?.lastKnownLocation));
    useEffect(() => {
        alive.current = true;
        return () => {
            alive.current = false;
            clearTimeout(operation.current?.timer);
            operation.current?.controller.abort();
            operation.current = null;
        };
    }, []);

    async function send(type) {
        if (operation.current || !allowed(latest.current)) return;
        if (type === 'Chime' && latest.current.unit?.UnitModel === 'myConnect Air') return;
        const op = { controller: new AbortController(), timer: null };
        operation.current = op; // Synchronous guard, including rapid repeated taps.
        const current = () => alive.current && operation.current === op;
        const finish = message => {
            if (!current()) return;
            clearTimeout(op.timer);
            operation.current = null;
            setBusy(null);
            setFeedback(message);
        };
        const baseline = reportKey(data);
        setBusy(type);
        setFeedback(null);
        try {
            await api.post('/api/unit/reportLocation', { UnitSerNum: unit.UnitSerNum, LocateType: type }, { signal: op.controller.signal });
            if (!current()) return;
            setFeedback({ severity: 'info', text: type === 'WiFi' ? 'Location should update within 30 seconds.' : 'Chime should start within 30 seconds.' });
            let attempts = 0;
            async function poll() {
                if (!current()) return;
                try {
                    const updated = await reload();
                    if (!current()) return;
                    attempts += 1;
                    if (!allowed(latest.current)) {
                        finish(null);
                    } else if (type === 'WiFi' && updated && reportKey(updated) !== baseline) {
                        finish({ severity: 'success', text: 'Device status updated.' });
                    } else if (attempts >= (type === 'Chime' ? 3 : 6)) {
                        finish({ severity: 'info', text: type === 'WiFi'
                            ? 'No new location report received yet. The last saved location is still shown.'
                            : 'Chime request sent. Check the device to confirm it sounded.' });
                    } else op.timer = setTimeout(poll, 10000);
                } catch {
                    finish({ severity: 'warning', text: 'The request was sent, but device status could not be refreshed. Check your connection before trying again.' });
                }
            }
            op.timer = setTimeout(poll, 10000);
        } catch {
            finish({ severity: 'error', text: 'Unable to confirm the request. Check your connection and device before trying again.' });
        }
    }

    if (!allowed({ unit, data })) return null;
    return <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Button onClick={() => send('WiFi')} disabled={Boolean(busy)} variant="contained" startIcon={<MyLocationIcon />}
                sx={{ flex: '1 1 220px', minHeight: 48 }}>
                {busy === 'WiFi' ? 'Updating Location...' : 'Update Location(Silent)'}
            </Button>
            {unit.UnitModel !== 'myConnect Air' && <Button onClick={() => send('Chime')} disabled={Boolean(busy)} variant="outlined" startIcon={<VolumeUpIcon />}
                sx={{ flex: '1 1 220px', minHeight: 48 }}>
                {busy === 'Chime' ? 'Chiming...' : 'Chime To Locate'}
            </Button>}
        </Box>
        {feedback && <Alert severity={feedback.severity} sx={{ mt: 1.5 }}>{feedback.text}</Alert>}
    </Box>;
}
